import { gameState } from "appworks/model/game-state";
import { Connector } from "appworks/server/connectors/connector";
import { Services } from "appworks/services/services";
import { Contract } from "appworks/utils/contracts/contract";
import Logger = require("js-logger");
import { PartnerConnectSpinRequestPayload } from "partner-connect/integration/schema/partner-connect-spin-schema";
import { PartnerConnect, PartnerConnectManager } from "partner-connect/partner-connect-manager";
import { FreespinRecord } from "slotworks/model/gameplay/records/freespin-record";

export class PartnerConnectConnector implements Connector {
    public test: boolean = false;
    public cheat?: string;

    public pendingCmds: string[] = [];
    public pendingStep?: number;

    public offlineResponse: PartnerConnect.SpinResponse;

    private onErrorMethod?: (message: string, fatal: boolean) => void;

    public onError(method: (message: string, fatal: boolean) => void): void {
        this.onErrorMethod = method;
    }

    public sendRequest(request: PartnerConnectSpinRequestPayload): Contract<PartnerConnect.SpinResponse> {
        return new Contract((resolve) => {
            if (this.offlineResponse) {
                resolve(this.offlineResponse);
                this.offlineResponse = undefined;
                return;
            }

            switch (request.type) {
                case "history":
                case "restore":
                    const record = gameState.getCurrentGame()?.getCurrentRecord();
                    const response = PartnerConnectManager.getNextWageData();
                    if (response) {
                        const lastBet = response.wager.bets[response.wager.bets.length - 1];
                        const cmds = lastBet.eventdata.nextCmds;
                        if (cmds) {
                            this.pendingCmds = cmds.split(",");
                        } else {
                            this.pendingCmds = PartnerConnectManager.getConfig().settings.slotConfig.AllowedFirstCommands?.map((x: any) => x.cmd);
                        }
                        if (lastBet.step && cmds) {
                            this.pendingStep = lastBet.step + 1;
                        } else {
                            this.pendingStep = undefined;
                        }
                        Logger.debug("PartnerConnect response", response);
                        Logger.debug("PartnerConnect nextCmd", this.pendingCmds);
                        resolve(response);
                    } else {
                        Services.transaction.setBalance(gameState.getCurrentGame().balance);
                        if (record instanceof FreespinRecord || record.cashWon > 0) {
                            const message = Services.translations.get("press_back_to_exit_replay");
                            PartnerConnectManager.getGame().uiComponents().showPopup({ message });
                        }
                    }
                    break;
                case "play":
                default:
                    const coins: number = PartnerConnectManager.getConfig().settings.slotConfig.CoinsPerBet[0];
                    const sRequest: PartnerConnect.SpinRequest = {
                        amount: request.creditSize / 100,
                        lines: 1,
                        coin: request.creditSize / 100 / coins,
                        coins
                    };
                    if (request.cmd) {
                        sRequest.cmd = request.cmd;
                    }
                    if (request.mode) {
                        sRequest.mode = request.mode;
                    }
                    if (this.test) {
                        sRequest.test = true;
                        if (this.cheat !== undefined) {
                            sRequest.cheat = this.cheat;
                            this.cheat = undefined;
                        }
                    }

                    let promise: Promise<PartnerConnect.SpinResponse>;
                    if (this.pendingStep) {
                        sRequest.step = this.pendingStep;
                        Logger.debug("PartnerConnect play request", sRequest);
                        promise = PartnerConnectManager.getGame().play(sRequest);
                    } else {
                        Logger.debug("PartnerConnect spin request", sRequest);
                        promise = PartnerConnectManager.getGame().spin(sRequest);
                    }
                    promise.then((res) => {
                        const lastBet = res.wager.bets[res.wager.bets.length - 1];
                        const cmds = lastBet.eventdata.nextCmds;
                        const allowedFirstCmds = PartnerConnectManager.getConfig().settings.slotConfig.AllowedFirstCommands;
                        if (cmds) {
                            this.pendingCmds = cmds.split(",");
                        } else if (allowedFirstCmds) {
                            this.pendingCmds = allowedFirstCmds.map((x: any) => x.cmd);
                        }
                        if (lastBet.step && cmds) {
                            this.pendingStep = lastBet.step + 1;
                        } else {
                            this.pendingStep = undefined;
                        }
                        Logger.debug("PartnerConnect response", res);
                        Logger.debug("PartnerConnect nextCmd", this.pendingCmds);
                        resolve(res);
                    })
                        .catch((reason) => {
                            Logger.debug("PartnerConnect error", reason);
                            PartnerConnectManager.getEmitters()?.spinError();
                            resolve();
                        });
                    break;
                case "collect":
                    if (this.pendingStep === undefined) {
                        const wageResponse = PartnerConnectManager.getNextWageData();
                        if (wageResponse) {
                            const lastBet = wageResponse.wager.bets[wageResponse.wager.bets.length - 1];
                            if (lastBet.step) {
                                this.pendingStep = lastBet.step + 1;
                            } else {
                                this.pendingStep = 2;
                            }
                        }
                    }
                    const cRequest = { step: this.pendingStep };
                    Logger.debug("PartnerConnect collect request", cRequest);
                    PartnerConnectManager.getGame().collect(cRequest)
                        .then((res) => {
                            this.pendingCmds = PartnerConnectManager.getConfig().settings.slotConfig.AllowedFirstCommands?.map((x: any) => x.cmd);
                            Logger.debug("PartnerConnect response", res);
                            Logger.debug("PartnerConnect nextCmd", this.pendingCmds);
                            this.pendingStep = undefined;
                            resolve(res);
                        })
                        .catch((reason) => {
                            Logger.debug("PartnerConnect error", reason);
                            PartnerConnectManager.getEmitters()?.spinError();
                            resolve();
                        });
                    break;
            }
        });
    }

    public close(): void {
        return;
    }
}

export const partnerConnectConnector = new PartnerConnectConnector();
